import { faker } from "@faker-js/faker";
import noop from "lodash/noop";

import {
  fakeBaseUserType,
  fakeContactPreferences,
  fakeFullProfileResponse,
  fakeGooglePlacesAddress,
  fakeLanguages,
  fakePronouns,
} from "@/__mocks__/mockBaseTypes";
import { merge } from "@/__mocks__/mockHelpers";
import type { UserOverviewCardProps } from "@/ui/components/profile";
import type { BasicInfoSectionProps } from "@/ui/components/profile/edit/BasicInfoSection";
import type { ContactInfoSectionProps } from "@/ui/components/profile/edit/ContactInfoSection";
import type { OtherInfoSectionProps } from "@/ui/components/profile/edit/OtherInfoSection";
import { AnalyticsSource } from "@/ui/components/profile/UserOverviewCard";
import {
  formatAddress,
  getBasicInfo,
  getContactInfo,
  getOtherInfo,
} from "@/utils/user.util";

const fakeUserOverviewCardProps = (
  overrides?: DeepPartial<UserOverviewCardProps>,
): UserOverviewCardProps => {
  const props: UserOverviewCardProps = {
    address: formatAddress({ address: fakeGooglePlacesAddress() }),
    analyticsSource: AnalyticsSource.ProfileTab,
    contactPreferences: fakeContactPreferences().join(", "),
    fullName: faker.person.fullName(),
    gender: faker.person.gender(),
    languages: fakeLanguages().join(", "),
    phoneNumber: faker.phone.number({ style: "national" }),
    pronouns: fakePronouns({}),
    selectedUserId: faker.string.uuid(),
    userType: fakeBaseUserType(),
  };
  return merge(props, overrides);
};

const fakeBasicInfoSectionProps = (
  overrides?: DeepPartial<BasicInfoSectionProps>,
): BasicInfoSectionProps => {
  const props: BasicInfoSectionProps = {
    ...getBasicInfo(fakeFullProfileResponse()),
    onChange: noop,
    userPermissions: () => "view",
  };
  return merge(props, overrides);
};

const fakeContactInfoSectionProps = (
  overrides?: DeepPartial<ContactInfoSectionProps>,
): ContactInfoSectionProps => {
  const props: ContactInfoSectionProps = {
    ...getContactInfo(fakeFullProfileResponse()),
    // ContactInfoSection uses a phone validation library that expects a certain format
    cellPhone: faker.phone.number({ style: "international" }),
    onChange: noop,
    userPermissions: () => "view",
  };
  return merge(props, overrides);
};

const fakeOtherInfoSectionProps = (
  overrides?: DeepPartial<OtherInfoSectionProps>,
): OtherInfoSectionProps => {
  const props: OtherInfoSectionProps = {
    ...getOtherInfo(fakeFullProfileResponse()),
    isAppUserSupport: faker.datatype.boolean(),
    onChange: noop,
    patientName: faker.person.fullName(),
    supports: faker.helpers.multiple(() => faker.person.fullName(), {
      count: { max: 10, min: 0 },
    }),
    userPermissions: () => "view",
  };
  return merge(props, overrides);
};
export {
  fakeBasicInfoSectionProps,
  fakeContactInfoSectionProps,
  fakeOtherInfoSectionProps,
  fakeUserOverviewCardProps,
};
